import { Badge, Button, Card, CardBody, CardHeader, FormField, Modal, Select } from '@chopchop-se/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Stocktake, StocktakeStatus } from '@warehouse/shared';
import { ClipboardList, Plus, Tablet } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { useAuthStore, userHasPermission } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

export const STOCKTAKE_TONE: Record<StocktakeStatus, 'neutral' | 'brand' | 'warning' | 'success'> = {
  draft: 'neutral',
  counting: 'brand',
  review: 'warning',
  posted: 'success',
};

/** Stocktake list + the create dialog. */
export function StocktakingPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const canPlan = userHasPermission(user, permissions, ['warehouse.inventory.task']);
  const [creating, setCreating] = useState(false);

  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.stocktakes(listQueryKey(state)),
    queryFn: () => warehouseApi.listStocktakes(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['stocktakes'], warehouseKeys.stocktakes());

  const columns: DataTableColumn<Stocktake>[] = [
    {
      id: 'name',
      header: t('warehouseCore:stocktaking.name'),
      sortField: 'name',
      cell: (row) => <span className="text-sm text-ink">{row.name}</span>,
    },
    {
      id: 'status',
      header: t('warehouseCore:stocktaking.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={STOCKTAKE_TONE[row.status]}>
          {t(`warehouseCore:stocktaking.statuses.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'progress',
      header: t('warehouseCore:stocktaking.counted'),
      cell: (row) => (
        <span className="font-mono text-sm">
          {t('warehouseCore:stocktaking.progress', { counted: row.countedCount, total: row.lineCount })}
        </span>
      ),
    },
    {
      id: 'diffs',
      header: t('warehouseCore:stocktaking.diffs'),
      align: 'right',
      cell: (row) =>
        row.diffCount > 0 ? (
          <Badge tone="warning">{row.diffCount}</Badge>
        ) : (
          <span className="text-ink-muted">0</span>
        ),
    },
    {
      id: 'createdBy',
      header: t('warehouseCore:stocktaking.createdBy'),
      cell: (row) => row.createdByName ?? '—',
      hideBelow: 'lg',
    },
    {
      id: 'createdAt',
      header: t('warehouseCore:stocktaking.createdAt'),
      sortField: 'createdAt',
      cell: (row) => formatDateTime(row.createdAt),
      hideBelow: 'md',
    },
  ];

  return (
    <>
      <PageHeader
        title={t('warehouseCore:stocktaking.title')}
        subtitle={t('warehouseCore:stocktaking.subtitle')}
        actions={
          <>
            <Link to="/stocktaking">
              <Button variant="secondary" size="sm" icon={<Tablet />}>
                {t('warehouseCore:stocktaking.openCounting')}
              </Button>
            </Link>
            {canPlan ? (
              <Button
                variant="primary"
                size="sm"
                icon={<Plus />}
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t('warehouseCore:stocktaking.new')}
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isFetching}
        error={error}
        emptyTitle={t('warehouseCore:stocktaking.empty')}
        emptyDescription={t('warehouseCore:stocktaking.emptyHelp')}
        onRowClick={(row) => {
          void navigate(`/warehouse/stocktaking/${row.id}`);
        }}
        filters={
          <Select
            aria-label={t('warehouseCore:stocktaking.status')}
            className="w-44"
            value={String(state.filters?.status ?? '')}
            options={[
              { value: '', label: t('warehouseCore:common.all') },
              ...(['draft', 'counting', 'review', 'posted'] as const).map((status) => ({
                value: status,
                label: t(`warehouseCore:stocktaking.statuses.${status}`),
              })),
            ]}
            onChange={(event) => {
              setFilter('status', event.target.value || undefined);
            }}
          />
        }
      />

      {creating ? (
        <CreateStocktakeDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}

function CreateStocktakeDialog({ onClose }: { onClose: () => void }): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [areaId, setAreaId] = useState('');

  const { data: areas } = useQuery({
    queryKey: warehouseKeys.areas(),
    queryFn: () => warehouseApi.listAreas(),
  });

  const create = useMutation({
    mutationFn: () =>
      warehouseApi.createStocktake({
        name: name.trim(),
        startNow: true,
        ...(areaId ? { scope: { areaIds: [areaId], binIds: [], articleIds: [] } } : {}),
      }),
    onSuccess: (stocktake) => {
      toastSuccess(t('warehouseCore:stocktaking.created'));
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      onClose();
      void navigate(`/warehouse/stocktaking/${stocktake.id}`);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('warehouseCore:stocktaking.new')} width={520}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('warehouseCore:stocktaking.name')}
          hint={t('warehouseCore:stocktaking.nameHint')}
          value={name}
          autoFocus
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <Select
          label={t('warehouseCore:stocktaking.scopeAreas')}
          value={areaId}
          options={[
            { value: '', label: t('warehouseCore:stocktaking.scopeAll') },
            ...(areas ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` })),
          ]}
          onChange={(e) => {
            setAreaId(e.target.value);
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="primary"
            icon={<ClipboardList />}
            loading={create.isPending}
            disabled={name.trim().length === 0}
            onClick={() => {
              create.mutate();
            }}
          >
            {t('common:actions.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
